import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { humanizeSupabaseError } from "@/lib/supabase-errors";
import { resolveWorkspaceMemberLabel } from "@/lib/workspace-member-display";
import { normalizeSearch } from "@/lib/normalize-search";
import { getMemberBadgeColors } from "@/lib/member-colors";

interface WorkspaceMember {
  id: string;
  user_id: string;
  role: string;
  display_name?: string | null;
  default_funnel_id?: string | null;
  default_stage_slug?: string | null;
  profile?: {
    full_name: string | null;
    avatar_url: string | null;
  };
}

type FunnelStage = {
  slug: string;
  name: string;
  order_index: number;
};

type SalesFunnelOption = {
  id: string;
  name: string;
  order_index: number | null;
  is_default: boolean | null;
};

type AssignLeadResult = {
  lead_id: string;
  assigned_member_id: string;
  funnel_id: string | null;
  funnel_name: string | null;
  stage_slug?: string | null;
  stage_name?: string | null;
  moved_funnel: boolean;
};

interface LeadMemberAssignFieldProps {
  workspaceId: string;
  leadId: string;
  assignedMemberIds: string[];
  onAssign?: (memberIds: string[], memberLabels?: string[]) => void;
}

export function LeadMemberAssignField({
  workspaceId,
  leadId,
  assignedMemberIds,
  onAssign,
}: LeadMemberAssignFieldProps) {
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [funnels, setFunnels] = useState<SalesFunnelOption[]>([]);
  const [funnelNames, setFunnelNames] = useState<Map<string, string>>(new Map());
  const [stagesByFunnelId, setStagesByFunnelId] = useState<Map<string, FunnelStage[]>>(
    new Map(),
  );
  const [allowedFunnelsByMemberId, setAllowedFunnelsByMemberId] = useState<
    Map<string, Set<string>>
  >(new Map());
  const [localAssignedIds, setLocalAssignedIds] = useState<string[]>(assignedMemberIds);
  const [open, setOpen] = useState(false);
  const [assigningMemberId, setAssigningMemberId] = useState<string | null>(null);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [membersLoadError, setMembersLoadError] = useState<string | null>(null);
  const [stagePickerMember, setStagePickerMember] = useState<WorkspaceMember | null>(null);
  const [stagePickerOpen, setStagePickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    setLocalAssignedIds(assignedMemberIds);
  }, [assignedMemberIds, leadId]);

  const memberDisplayName = useCallback(
    (member: WorkspaceMember) =>
      resolveWorkspaceMemberLabel(
        member.display_name,
        member.profile?.full_name,
      ),
    [],
  );

  const memberNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of members) {
      map.set(member.id, memberDisplayName(member));
    }
    return map;
  }, [members, memberDisplayName]);

  const notifyAssigneesChanged = useCallback(
    (ids: string[]) => {
      const labels = ids
        .map((id) => memberNameById.get(id))
        .filter((label): label is string => Boolean(label));
      onAssign?.(ids, labels);
    },
    [memberNameById, onAssign],
  );

  useEffect(() => {
    if (!workspaceId) return;

    let cancelled = false;

    const fetchMembers = async () => {
      setLoadingMembers(true);
      setMembersLoadError(null);

      try {
        const membersRes = await supabase
          .from("workspace_members")
          .select("id, user_id, role, display_name, default_funnel_id, default_stage_slug")
          .eq("workspace_id", workspaceId);

        if (membersRes.error) throw membersRes.error;

        const memberRows = membersRes.data ?? [];

        const funnelsRes = await supabase
          .from("sales_funnels")
          .select("id, name, order_index, is_default")
          .eq("workspace_id", workspaceId)
          .order("order_index", { ascending: true });

        if (funnelsRes.error) throw funnelsRes.error;

        const funnelRows = (funnelsRes.data || []) as SalesFunnelOption[];
        const allFunnelIds = funnelRows.map((f) => f.id);

        let stagesMap = new Map<string, FunnelStage[]>();
        if (allFunnelIds.length > 0) {
          const { data: stagesData } = await supabase
            .from("lead_stages")
            .select("slug, name, order_index, funnel_id")
            .in("funnel_id", allFunnelIds)
            .order("order_index", { ascending: true });

          stagesMap = (stagesData || []).reduce((acc, stage) => {
            if (!stage.funnel_id) return acc;
            const list = acc.get(stage.funnel_id) || [];
            list.push({
              slug: stage.slug,
              name: stage.name,
              order_index: stage.order_index,
            });
            acc.set(stage.funnel_id, list);
            return acc;
          }, new Map<string, FunnelStage[]>());
        }

        const memberIds = memberRows.map((m) => m.id);
        let allowedMap = new Map<string, Set<string>>();
        if (memberIds.length > 0) {
          const { data: memberFunnelRows } = await supabase
            .from("workspace_member_funnels")
            .select("member_id, funnel_id")
            .in("member_id", memberIds);

          allowedMap = (memberFunnelRows || []).reduce((acc, row) => {
            const set = acc.get(row.member_id) || new Set<string>();
            set.add(row.funnel_id);
            acc.set(row.member_id, set);
            return acc;
          }, new Map<string, Set<string>>());
        }

        const userIds = memberRows.map((m) => m.user_id);
        const { data: profiles } =
          userIds.length > 0
            ? await supabase
                .from("profiles")
                .select("id, full_name, avatar_url")
                .in("id", userIds)
            : { data: [] as { id: string; full_name: string | null; avatar_url: string | null }[] };

        if (cancelled) return;

        const profileMap = new Map(profiles?.map((p) => [p.id, p]) || []);
        setMembers(
          memberRows.map((m) => ({
            ...m,
            default_funnel_id: m.default_funnel_id ?? null,
            default_stage_slug: m.default_stage_slug ?? null,
            profile: (profileMap.get(m.user_id) as WorkspaceMember["profile"]) || null,
          })),
        );

        setFunnels(funnelRows);
        setFunnelNames(new Map(funnelRows.map((f) => [f.id, f.name])));
        setStagesByFunnelId(stagesMap);
        setAllowedFunnelsByMemberId(allowedMap);
      } catch (error: unknown) {
        if (cancelled) return;
        console.error("Erro ao carregar membros para atribuição:", error);
        setMembers([]);
        setFunnels([]);
        setAllowedFunnelsByMemberId(new Map());
        setMembersLoadError(
          humanizeSupabaseError(error, {
            fallback: "Não foi possível carregar a equipe. Tente novamente.",
          }),
        );
      } finally {
        if (!cancelled) setLoadingMembers(false);
      }
    };

    void fetchMembers();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const getAvailableFunnelsForMember = useCallback(
    (member: WorkspaceMember) => {
      if (member.role === "owner" || member.role === "admin") return funnels;
      const allowed = allowedFunnelsByMemberId.get(member.id);
      if (!allowed || allowed.size === 0) return funnels;
      return funnels.filter((funnel) => allowed.has(funnel.id));
    },
    [allowedFunnelsByMemberId, funnels],
  );

  const resolveDefaultStageLabel = useCallback(
    (member: WorkspaceMember) => {
      if (!member.default_funnel_id) return null;
      const stages = stagesByFunnelId.get(member.default_funnel_id) || [];
      if (member.default_stage_slug) {
        return (
          stages.find((s) => s.slug === member.default_stage_slug)?.name ||
          member.default_stage_slug
        );
      }
      return stages[0]?.name || "Primeira coluna";
    },
    [stagesByFunnelId],
  );

  const handleRemoveMember = async (memberId: string) => {
    if (assigningMemberId) return;
    setAssigningMemberId(memberId);
    try {
      const { error } = await supabase.rpc("remove_lead_assignee", {
        p_lead_id: leadId,
        p_member_id: memberId,
      });
      if (error) throw error;

      const next = localAssignedIds.filter((id) => id !== memberId);
      setLocalAssignedIds(next);
      notifyAssigneesChanged(next);

      const name = memberNameById.get(memberId) ?? "Membro";
      toast({
        title: "Membro removido",
        description: `${name} foi desatribuído deste lead.`,
      });
    } catch (error: unknown) {
      toast({
        title: "Erro ao remover atribuição",
        description: humanizeSupabaseError(error),
        variant: "destructive",
      });
    } finally {
      setAssigningMemberId(null);
    }
  };

  const handleAssignToMember = async (
    member: WorkspaceMember,
    options?: { funnelId?: string | null; stageSlug?: string | null },
  ) => {
    if (assigningMemberId) return;

    setAssigningMemberId(member.id);
    try {
      try {
        const { data: meMember } = await supabase
          .from("workspace_members")
          .select("id")
          .eq("workspace_id", workspaceId)
          .eq("user_id", (await supabase.auth.getUser()).data.user?.id ?? "")
          .maybeSingle();
        if (meMember?.id) {
          await supabase.rpc("set_acting_member_id", { _member_id: meMember.id });
        }
      } catch {
        /* opcional */
      }

      const rpcArgs: {
        p_lead_id: string;
        p_target_member_id: string;
        p_funnel_id?: string;
        p_stage_slug?: string;
      } = {
        p_lead_id: leadId,
        p_target_member_id: member.id,
      };

      if (options?.funnelId) rpcArgs.p_funnel_id = options.funnelId;
      if (options?.stageSlug) rpcArgs.p_stage_slug = options.stageSlug;

      const { data, error } = await supabase.rpc("assign_lead_to_member", rpcArgs);
      if (error) throw error;

      const result = data as AssignLeadResult | null;
      const funnelName =
        result?.funnel_name ||
        (result?.funnel_id ? funnelNames.get(result.funnel_id) : null) ||
        (member.default_funnel_id ? funnelNames.get(member.default_funnel_id) : null);
      const stageName =
        result?.stage_name ||
        (result?.stage_slug
          ? stagesByFunnelId
              .get(result.funnel_id || member.default_funnel_id || "")
              ?.find((s) => s.slug === result.stage_slug)?.name
          : null);

      const next = localAssignedIds.includes(member.id)
        ? localAssignedIds
        : [...localAssignedIds, member.id];
      setLocalAssignedIds(next);
      notifyAssigneesChanged(next);
      setOpen(false);
      setStagePickerOpen(false);
      setStagePickerMember(null);

      let description = "Lead atribuído ao membro da equipe.";
      if (result?.moved_funnel && funnelName && stageName) {
        description = `Lead atribuído e movido para "${funnelName}" → ${stageName}.`;
      } else if (result?.moved_funnel && funnelName) {
        description = `Lead atribuído e movido para o funil "${funnelName}".`;
      }

      toast({
        title: "Membro atribuído",
        description,
      });
    } catch (error: unknown) {
      toast({
        title: "Erro ao atribuir",
        description: humanizeSupabaseError(error, {
          permissionMessage:
            "Você não tem permissão para atribuir ou mover este lead.",
          fallback: "Não foi possível atribuir o lead. Tente novamente.",
        }),
        variant: "destructive",
      });
    } finally {
      setAssigningMemberId(null);
    }
  };

  const handleMemberToggle = (member: WorkspaceMember) => {
    if (assigningMemberId) return;

    if (localAssignedIds.includes(member.id)) {
      void handleRemoveMember(member.id);
      return;
    }

    if (getAvailableFunnelsForMember(member).length > 0 && localAssignedIds.length === 0) {
      setStagePickerMember(member);
      setStagePickerOpen(true);
      setOpen(false);
      return;
    }

    void handleAssignToMember(member);
  };

  const initials = (name: string) =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const assignedSet = useMemo(() => new Set(localAssignedIds), [localAssignedIds]);

  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return members;
    const q = normalizeSearch(searchQuery);
    return members.filter((member) =>
      normalizeSearch(memberDisplayName(member)).includes(q),
    );
  }, [members, memberDisplayName, searchQuery]);

  const stagePickerContent = stagePickerMember ? (
    <div className="space-y-1 max-h-[min(50vh,320px)] overflow-y-auto">
      <p className="text-sm text-muted-foreground px-1 pb-2">
        Escolha o funil e a coluna de destino para{" "}
        <span className="font-medium text-foreground">
          {memberDisplayName(stagePickerMember)}
        </span>
        .
      </p>
      {stagePickerMember.default_funnel_id && (
        <Button
          type="button"
          variant="ghost"
          disabled={!!assigningMemberId}
          onClick={() => handleAssignToMember(stagePickerMember)}
          className="w-full justify-start gap-2 h-auto py-2"
        >
          Coluna padrão ({funnelNames.get(stagePickerMember.default_funnel_id)} →{" "}
          {resolveDefaultStageLabel(stagePickerMember)})
          {assigningMemberId === stagePickerMember.id && (
            <Loader2 className="ml-auto h-4 w-4 animate-spin" />
          )}
        </Button>
      )}
      {getAvailableFunnelsForMember(stagePickerMember).map((funnel) => {
        const stages = stagesByFunnelId.get(funnel.id) || [];
        return (
          <div key={funnel.id} className="space-y-0.5">
            <p className="px-1 pt-2 text-xs font-medium text-muted-foreground">{funnel.name}</p>
            {stages.length === 0 ? (
              <Button
                type="button"
                variant="ghost"
                disabled={!!assigningMemberId}
                onClick={() =>
                  handleAssignToMember(stagePickerMember, { funnelId: funnel.id })
                }
                className="w-full justify-start h-9"
              >
                Primeira coluna
              </Button>
            ) : (
              stages.map((stage) => (
                <Button
                  key={`${funnel.id}:${stage.slug}`}
                  type="button"
                  variant="ghost"
                  disabled={!!assigningMemberId}
                  onClick={() =>
                    handleAssignToMember(stagePickerMember, {
                      funnelId: funnel.id,
                      stageSlug: stage.slug,
                    })
                  }
                  className="w-full justify-start h-9"
                >
                  {stage.name}
                </Button>
              ))
            )}
          </div>
        );
      })}
    </div>
  ) : null;

  return (
    <div className="space-y-2">
      {localAssignedIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {localAssignedIds.map((memberId) => {
            const name = memberNameById.get(memberId) ?? "Membro da equipe";
            const isRemoving = assigningMemberId === memberId;
            const badgeColors = getMemberBadgeColors(memberId);
            return (
              <Badge
                key={memberId}
                variant="secondary"
                className={cn(
                  "gap-1 border-transparent py-1 pl-2 pr-1 text-xs font-normal",
                  "bg-[var(--member-badge-bg)] text-[var(--member-badge-fg)]",
                  "dark:bg-[var(--member-badge-bg-dark)] dark:text-[var(--member-badge-fg-dark)]",
                )}
                style={
                  {
                    "--member-badge-bg": badgeColors.light.bg,
                    "--member-badge-fg": badgeColors.light.fg,
                    "--member-badge-bg-dark": badgeColors.dark.bg,
                    "--member-badge-fg-dark": badgeColors.dark.fg,
                  } as CSSProperties
                }
              >
                <span className="max-w-[160px] truncate">{name}</span>
                <button
                  type="button"
                  className="rounded-sm p-0.5 opacity-70 transition-opacity hover:opacity-100 disabled:opacity-50"
                  disabled={isRemoving}
                  aria-label={`Remover ${name}`}
                  onClick={() => void handleRemoveMember(memberId)}
                >
                  {isRemoving ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <X className="h-3 w-3" />
                  )}
                </button>
              </Badge>
            );
          })}
        </div>
      )}

      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setSearchQuery("");
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
            disabled={loadingMembers && members.length === 0}
          >
            {loadingMembers && members.length === 0 ? (
              <span className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Carregando equipe...
              </span>
            ) : localAssignedIds.length > 0 ? (
              "Adicionar ou remover membros"
            ) : (
              "Selecionar membros da equipe..."
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Buscar membro..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {membersLoadError ? (
                <p className="px-3 py-4 text-center text-sm text-destructive">{membersLoadError}</p>
              ) : (
                <>
                  <CommandEmpty>Nenhum membro encontrado.</CommandEmpty>
                  <CommandGroup>
                    {filteredMembers.map((member) => {
                      const name = memberDisplayName(member);
                      const isSelected = assignedSet.has(member.id);
                      const isAssigning = assigningMemberId === member.id;
                      return (
                        <CommandItem
                          key={member.id}
                          value={member.id}
                          disabled={!!assigningMemberId}
                          onSelect={() => handleMemberToggle(member)}
                          className="flex items-center gap-2"
                        >
                          <Avatar className="h-7 w-7 shrink-0">
                            {member.profile?.avatar_url ? (
                              <img
                                src={member.profile.avatar_url}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <AvatarFallback className="bg-primary/10 text-xs text-primary">
                                {initials(name)}
                              </AvatarFallback>
                            )}
                          </Avatar>
                          <span className="min-w-0 flex-1 truncate">{name}</span>
                          {isAssigning ? (
                            <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                          ) : (
                            <Check
                              className={cn(
                                "h-4 w-4 shrink-0",
                                isSelected ? "opacity-100" : "opacity-0",
                              )}
                            />
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Dialog
        open={stagePickerOpen}
        onOpenChange={(next) => {
          setStagePickerOpen(next);
          if (!next) setStagePickerMember(null);
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Escolher coluna</DialogTitle>
          </DialogHeader>
          {stagePickerContent}
        </DialogContent>
      </Dialog>
    </div>
  );
}
